import type {
  Bookmark,
  ChatModelOption,
  CustomersResponse,
  HistoryThread,
  HistoryTurn,
  Me,
  PopularQuestion,
  QueryDone,
  RecentDoc,
  ShareCreated,
  ShareLink,
  SharedConversation,
  SkillResult,
} from "./types";
import { getAnswerOptions } from "@/lib/settings";

const BASE = "/api/chat";

export class UnauthorizedError extends Error {}

// ── 세션 만료 → 자동 로그오프(중앙 처리) ──
// 서버 세션이 만료되면 어떤 API든 401을 돌려준다. 과거에는 호출부마다 토스트만
// 띄워 "화면은 보이는데 기능만 깨지는" 상태가 됐다. 이제 401을 한 곳에서 받아
// 즉시 로그인 화면으로 전환(자동 로그오프)한다. 중복 리다이렉트는 플래그로 가드.
let _sessionExpired = false;

export function isSessionExpired(): boolean {
  return _sessionExpired;
}

/** 세션 만료 처리: 한 번만 로그인 화면으로 보낸다(현재 경로를 redirect_to로 보존). */
export function handleSessionExpired(): void {
  if (_sessionExpired) return;
  _sessionExpired = true;
  const redirect = window.location.pathname + window.location.search;
  window.location.href = `${BASE}/login?redirect_to=${encodeURIComponent(redirect)}`;
}

async function req<T>(path: string, init?: RequestInit, opts?: { public?: boolean }): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    ...init,
  });
  if (res.status === 401) {
    if (!opts?.public) handleSessionExpired(); // 보호된 호출 → 자동 로그오프
    throw new UnauthorizedError("UNAUTHENTICATED");
  }
  if (res.status === 429) throw new Error("요청이 너무 잦습니다. 잠시 후 다시 시도해 주세요.");
  if (!res.ok) {
    let msg = `요청 실패 (${res.status})`;
    try {
      const j = await res.json();
      // HTTPException(503, "문구") 는 {"detail": "문구"} — 문자열 detail 도 그대로 보여 준다.
      // (422 검증 오류의 detail 은 배열이라 여기에 걸리지 않는다)
      const detail = typeof j?.detail === "string" ? j.detail : j?.detail?.message;
      msg = detail || j?.message || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  // ⚠ me()는 public — 401이어도 자동 로그오프(SSO login 재진입) 금지.
  // 로그아웃 직후 "/" 진입 시 me() 401 → handleSessionExpired() → /login 자동 리다이렉트
  // → NW IdP 세션이 살아있으면(운영포털·다른 계정) 즉시 재인가되어 "로그아웃이 안 되는" 버그.
  // UnauthorizedError만 던져 Gate(App.tsx)가 로그인 화면을 렌더하게 한다(운영포털과 동일 동작).
  me: () => req<Me>("/me", undefined, { public: true }),
  customers: () => req<CustomersResponse>("/customers"),
  // 질의 모델 선택지(관리자 등록, 활성만). 빈 목록이면 선택 UI 미노출.
  models: () => req<{ items: ChatModelOption[] }>("/models"),
  announcement: () =>
    req<{ announcement: { id: number; message: string; level: string } | null }>("/announcement"),
  // sdocHint: 첨부 업로드 직후 질의(v3.8) — 서버가 세션문서 참조를 강제한다.
  // modelId: 사용자 선택 질의 모델(chat_model.id, v3.9) — null 이면 서버 기본 동작.
  query: (question: string, customer: string, threadId = "", sdocHint = false, modelId: number | null = null) =>
    req<QueryDone>("/query", {
      method: "POST",
      body: JSON.stringify({
        question,
        customer,
        thread_id: threadId,
        sdoc_hint: sdocHint,
        answer_model_id: modelId,
        ...getAnswerOptions(),
      }),
    }),
  // 참고문서 일부를 제외(deselect)하고 동일 질문 재생성 (웹 전용·비스트리밍)
  // modelId: 사용자 선택 질의 모델 — 재생성도 원 답변과 같은 모델로 (2026-09-30, 누락 수정)
  regenerate: (question: string, customer: string, threadId = "", excludeUrls: string[] = [],
               modelId: number | null = null) =>
    req<QueryDone>("/regenerate", {
      method: "POST",
      body: JSON.stringify({
        question,
        customer,
        thread_id: threadId,
        exclude_urls: excludeUrls,
        answer_model_id: modelId,
        ...getAnswerOptions(),
      }),
    }),
  confirm: (confirm_token: string) =>
    req<QueryDone>("/confirm", { method: "POST", body: JSON.stringify({ confirm_token }) }),
  decline: (confirm_token: string) =>
    req<QueryDone>("/decline", { method: "POST", body: JSON.stringify({ confirm_token }) }),
  save: (session_id: string) =>
    req<{ status: string; page_url?: string; message?: string }>("/save", {
      method: "POST",
      body: JSON.stringify({ session_id }),
    }),
  feedback: (query_id: string, feedback: 1 | -1, comment = "") =>
    req<{ upserted: boolean }>("/feedback", {
      method: "POST",
      body: JSON.stringify({ query_id, feedback, comment }),
    }),
  suggest: (question: string, answer: string) =>
    req<{ suggestions: string[] }>("/suggest", {
      method: "POST",
      body: JSON.stringify({ question, answer }),
    }),
  upload: async (file: File, threadId = "", hwocr = false) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("thread_id", threadId);
    // 손글씨·저품질 스캔 지정 → 서버가 HWOCR(LLM 비전)로 강제 판독
    fd.append("hwocr", hwocr ? "1" : "0");
    const res = await fetch(`${BASE}/upload`, { method: "POST", credentials: "same-origin", body: fd });
    if (res.status === 401) { handleSessionExpired(); throw new UnauthorizedError("UNAUTHENTICATED"); }
    if (!res.ok) throw new Error(`업로드 실패 (${res.status})`);
    return res.json() as Promise<{ status: string; title?: string; chars?: number; message?: string }>;
  },
  // ── 스킬(회의록·요약·번역·비교 등) — 다중 파일 지원 ──
  // customer·sourceQueryId 는 양식 문서 작성(doc_fill) 전용 — 격리 검색 고객사 / '직전 답변' query_id
  // modelId: 사용자 선택 질의 모델(chat_model.id) — null 이면 서버 기본 스킬 모델
  skill: async (files: File[], text: string, threadId = "", actionHint = "", customer = "", sourceQueryId = "",
                modelId: number | null = null) => {
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    fd.append("text", text);
    fd.append("thread_id", threadId);
    fd.append("action_hint", actionHint);
    fd.append("customer", customer);
    fd.append("source_query_id", sourceQueryId);
    if (modelId != null) fd.append("answer_model_id", String(modelId));
    const res = await fetch(`${BASE}/skill`, { method: "POST", credentials: "same-origin", body: fd });
    if (res.status === 401) { handleSessionExpired(); throw new UnauthorizedError("UNAUTHENTICATED"); }
    if (!res.ok) throw new Error(`스킬 실행 실패 (${res.status})`);
    return res.json() as Promise<SkillResult>;
  },
  // ── 개인 북마크(즐겨찾기) ──
  bookmarkAdd: (b: { query_id?: string; question?: string; answer: string; customer?: string; note?: string }) =>
    req<{ id: number; status: string }>("/bookmark", { method: "POST", body: JSON.stringify(b) }),
  bookmarks: () => req<{ items: Bookmark[] }>("/bookmarks"),
  bookmarkDelete: (id: number) => req<{ status: string }>(`/bookmark/${id}`, { method: "DELETE" }),
  // ── 대화 공유 링크 ──
  shareCreate: (
    title: string,
    turns: { role: "user" | "bot"; text: string }[],
    opts?: { expires_days?: number; require_login?: boolean },
  ) =>
    req<ShareCreated>("/share", {
      method: "POST",
      body: JSON.stringify({
        title,
        turns,
        expires_days: opts?.expires_days ?? 30,
        require_login: opts?.require_login ?? false,
      }),
    }),
  shares: () => req<{ items: ShareLink[] }>("/shares"),
  shareRevoke: (token: string) =>
    req<{ status: string }>(`/share/${encodeURIComponent(token)}`, { method: "DELETE" }),
  shared: (token: string) =>
    req<SharedConversation>(`/shared/${encodeURIComponent(token)}`, undefined, { public: true }), // 공개(무인증) — 401이어도 자동 로그오프 금지
  // ── 첫 화면 위젯 ──
  popular: () => req<{ items: PopularQuestion[] }>("/popular"),
  recentDocs: () => req<{ items: RecentDoc[] }>("/recent-docs"),
  // 추천 질문 칩 — 서버 SUGGESTED_QUESTIONS_ENABLED off(기본) 면 빈 목록
  suggestions: () => req<{ items: string[] }>("/suggestions"),
  // ── 대화 이력 ──
  history: () => req<{ items: HistoryThread[] }>("/history"),
  historyDetail: (threadId: string) =>
    req<{ thread_id: string; turns: HistoryTurn[] }>(`/history/${encodeURIComponent(threadId)}`),
  historySearch: (q: string) =>
    req<{ items: (HistoryTurn & { thread_id: string })[] }>(`/history/search?q=${encodeURIComponent(q)}`),
  historyDelete: (threadId: string) =>
    req<{ status: string }>(`/history/${encodeURIComponent(threadId)}`, { method: "DELETE" }),
  historyRename: (threadId: string, title: string) =>
    req<{ status: string; thread_id: string; title: string | null }>(
      `/history/${encodeURIComponent(threadId)}`,
      { method: "PATCH", body: JSON.stringify({ title }) },
    ),
  // ── 받아쓰기(다글로 STT) — 음성 파일 → 텍스트 변환 ──
  sttHealth: () => req<{ status: string; enabled: boolean }>("/stt/health"),
  sttJobs: () => req<{ items: SttJob[] }>("/stt/jobs"),
  sttJob: (id: string) => req<SttJobDetail>(`/stt/jobs/${encodeURIComponent(id)}`),
  sttDelete: (id: string) =>
    req<{ status: string }>(`/stt/jobs/${encodeURIComponent(id)}`, { method: "DELETE" }),
  sttDownloadUrl: (id: string) => `${BASE}/stt/jobs/${encodeURIComponent(id)}/download`,
  // 업로드는 진행률 표시가 필요해 XHR 사용(fetch 는 업로드 progress 미지원).
  sttUpload: (
    file: File,
    opts: { timestamps: boolean; diarization: boolean },
    onProgress?: (pct: number) => void,
  ): Promise<SttJob> =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${BASE}/stt/jobs`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status === 401) {
          handleSessionExpired();
          reject(new UnauthorizedError("UNAUTHENTICATED"));
          return;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText) as SttJob);
          } catch {
            reject(new Error("응답 파싱 실패"));
          }
          return;
        }
        let msg = `업로드 실패 (${xhr.status})`;
        try {
          const j = JSON.parse(xhr.responseText);
          const d = j?.detail?.message || j?.detail || j?.message;
          if (typeof d === "string" && d) msg = d;
        } catch {
          /* ignore */
        }
        reject(new Error(msg));
      };
      xhr.onerror = () => reject(new Error("네트워크 오류로 업로드에 실패했습니다."));
      const fd = new FormData();
      fd.append("file", file);
      fd.append("timestamps", opts.timestamps ? "1" : "0");
      fd.append("diarization", opts.diarization ? "1" : "0");
      xhr.send(fd);
    }),
  // ── 개인 메일 연동 ──
  // owner_email 은 서버가 세션에서 주입한다 — 여기서 보내지 않는다(본인 계정만 접근).
  mailStatus: () => req<MailStatus>("/mail/status"),
  mailAccount: () => req<{ account: MailAccount | null }>("/mail/account"),
  mailSave: (body: MailAccountInput) =>
    req<{ account: MailAccount }>("/mail/account", { method: "PUT", body: JSON.stringify(body) }),
  // 저장하지 않고 연결만 확인. secret 이 null 이면 서버에 저장된 비밀번호로 시도.
  mailTest: (secret: string | null) =>
    req<MailTestResult>("/mail/account/test", { method: "POST", body: JSON.stringify({ secret }) }),
  // 연동 해제 + 데이터 전체 파기. 대상 계정 id 는 서버가 세션으로 정한다.
  mailUnlink: () => req<MailPurgeResult>("/mail/account", { method: "DELETE" }),
  // ── 메일 업무비서(mail_task) — 본인 업무만. owner 는 서버가 세션에서 주입 ──
  taskSummary: () => req<TaskSummary>("/mail/tasks/summary"),
  taskList: (p: TaskListParams = {}) => {
    const qs = new URLSearchParams();
    Object.entries(p).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    const s = qs.toString();
    return req<TaskListResult>(`/mail/tasks${s ? `?${s}` : ""}`);
  },
  taskPatch: (id: number, body: TaskPatchInput) =>
    req<{ task: MailTask }>(`/mail/tasks/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  taskPrefs: () => req<{ pref: TaskPref }>("/mail/tasks/prefs"),
  taskPrefsSave: (body: Partial<TaskPref>) =>
    req<{ pref: TaskPref }>("/mail/tasks/prefs", { method: "PUT", body: JSON.stringify(body) }),
  loginUrl: (redirectTo = "/") => `${BASE}/login?redirect_to=${encodeURIComponent(redirectTo)}`,
  logout: () => req<void>("/logout", { method: "POST" }),
};

// ── 개인 메일 연동 타입 ──
export type MailStatusValue = "ok" | "auth_error" | "bs_user_missing" | "never_synced";

export interface MailStatus {
  enabled: boolean;
  search_enabled?: boolean;
  /** 서버가 요구하는 동의 문구 버전(PERSONAL_MAIL_CONSENT_VERSION). 없으면 화면 기본값 사용. */
  consent_version?: number;
  contact?: string;
  secret_ready?: boolean;
  max_months?: number;
  max_retention_months?: number;
  /** 메일 업무비서(mail_task) 기능 on/off — 옵트인 토글·/tasks 진입 노출 제어. */
  task_feature?: boolean;
  /** 내메일 전용 검색 스코프 — 서버가 켜져 있을 때만 내려온다. 연동 사용자에게만 셀렉터에 노출. */
  my_mail_scope?: { value: string; label: string } | null;
  /** 첨부파일 저장 옵트인 기능 on/off — '첨부파일도 저장' 토글과 동의 문구 1항을 바꾼다. */
  attach_optin?: boolean;
}

// ── 메일 업무비서 타입 ──
export type TaskStatus = "open" | "done" | "dismissed" | "snoozed";
export type DueConfidence = "high" | "medium" | "low";

export interface MailTask {
  id: number;
  thread_key: string;
  bs_page_id: number;
  page_url: string; // 지식저장소 원문 스레드(본인 전용 책)
  subject: string;
  task_title: string;
  request_type: string;
  origin: "customer" | "internal" | string;
  due_date: string; // '' = 기한 없음
  due_confidence: DueConfidence | string;
  due_text: string;
  customer: string;
  requester_email: string;
  requester_name: string;
  received_at: string;
  has_attachment: boolean;
  attach_count: number;
  status: TaskStatus | string;
  replied_at: string;
  done_at: string;
  snoozed_until: string;
  needs_review: boolean;
  user_edited: boolean;
  days_left: number | null;
}

export interface TaskListParams {
  status?: "open" | "done" | "dismissed" | "snoozed" | "all";
  customer?: string;
  request_type?: string;
  due?: "any" | "imminent" | "none" | "has";
  q?: string;
  limit?: number;
  offset?: number;
}

export interface TaskListResult {
  items: MailTask[];
  total: number;
  today: string;
}

export interface TaskPatchInput {
  status?: TaskStatus;
  snoozed_until?: string;
  due_date?: string; // '' = 기한 지우기
  request_type?: string;
  customer?: string;
  task_title?: string;
}

export interface TaskPref {
  notify_enabled: boolean;
  quiet_weekend: boolean;
}

export interface NameCount {
  name: string;
  count: number;
}

export interface TaskSummary {
  today: string;
  kpi: {
    open: number;
    due_today: number;
    due_week: number;
    overdue: number;
    needs_review: number;
    no_due: number;
  };
  by_customer: NameCount[];
  by_type: NameCount[];
  by_type_30d: NameCount[];
  by_type_7d: NameCount[];
  by_customer_30d: NameCount[];
  by_origin_30d: { customer: number; internal: number };
  imminent: MailTask[];
  last_extract_at: string;
  pending_queue: number;
  request_types: string[];
  /** 받은 메일 건수(최근 30일, 업무가 아니라 메일 통 수). null/미존재 = 기능 off → 카드 숨김 (2026-09-30) */
  mail_stats?: {
    total_30d: number;
    by_customer_30d: NameCount[];
    by_type_30d: NameCount[];
    since: string; // 집계 시작일(기능을 켠 뒤 첫 수신일). 빈 값이면 아직 데이터 없음
  } | null;
  pref: TaskPref;
}

export interface MailFolders {
  inbox: boolean;
  sent: boolean;
}

export interface MailExclude {
  subject: string[];
  from: string[];
  /** 저장 차단 단어 — 제목·본문 어디에든 있으면 아예 저장하지 않는다. */
  keywords: string[];
}

export interface MailAccount {
  id: number;
  owner_email: string;
  owner_name: string;
  has_secret: boolean; // 비밀번호 자체는 서버가 절대 내려주지 않는다(보유 여부만)
  status: MailStatusValue | string;
  paused: boolean;
  folders: MailFolders;
  since_months: number;
  retention_months: number; // 0 = 무제한
  exclude: MailExclude;
  consent_version: number;
  consent_at: string | null;
  last_sync_at: string | null;
  thread_count: number;
  last_error: string;
  /** 메일 업무비서 옵트인 — 기능 off 면 항상 false 로 내려온다. */
  task_enabled?: boolean;
  /** 첨부파일 저장 여부 — 옵트인 기능 off 면 기존 동작(항상 저장)이라 true 로 내려온다. */
  attach_enabled?: boolean;
}

export interface MailAccountInput {
  secret: string | null; // null = 기존 비밀번호 유지
  folders: MailFolders;
  since_months: number;
  retention_months: number;
  exclude: MailExclude;
  consent_version: number;
  paused: boolean;
  /** 메일 업무비서 옵트인(계정 단위). 기능 off 면 서버가 false 로 저장한다. */
  task_enabled: boolean;
  /** 첨부파일 저장 옵트인(2026-09-30). null = 서버 기존 값 유지(옵트인 기능 off 일 때). */
  attach_enabled: boolean | null;
}

export interface MailTestResult {
  ok: boolean;
  folders: string[];
  error_code: string;
}

export interface MailPurgeResult {
  purged: boolean;
  deleted?: Record<string, number>;
}

// ── 받아쓰기(다글로 STT) 타입 ──
export interface SttJob {
  id: string;
  filename: string;
  size_bytes: number | null;
  timestamps: boolean;
  diarization: boolean;
  status: "queued" | "submitted" | "processing" | "done" | "error";
  progress: number;
  error_msg: string;
  preview: string;
  created_at: string;
  completed_at: string | null;
}

export interface SttJobDetail extends SttJob {
  text: string;
}

export interface StreamProgress {
  phase: "map" | "reduce" | string;
  done: number;
  total: number;
  message: string;
}

export interface StreamHandlers {
  onChunk: (text: string) => void;
  onDone: (done: QueryDone) => void;
  onError: (message: string) => void;
  // 보고서 종합(SYNTH) 등 장시간 작업의 진행률(선택). 미지정이면 무시.
  onProgress?: (p: StreamProgress) => void;
}

/**
 * POST /api/chat/stream 을 fetch + ReadableStream 으로 구독한다.
 * (EventSource 는 GET 전용이라 직접 SSE 파싱)
 */
export async function streamQuery(
  question: string,
  customer: string,
  threadId: string,
  handlers: StreamHandlers,
  signal?: AbortSignal,
  sdocHint = false, // v3.8: 첨부 업로드 직후 질의 — 서버가 세션문서 참조를 강제
  modelId: number | null = null, // v3.9: 사용자 선택 질의 모델(chat_model.id)
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${BASE}/stream`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify({
        question,
        customer,
        thread_id: threadId,
        sdoc_hint: sdocHint,
        answer_model_id: modelId,
        ...getAnswerOptions(),
      }),
      signal,
    });
  } catch (e) {
    if ((e as { name?: string })?.name === "AbortError") return; // 사용자 중지 — 폴백 금지
    handlers.onError("network");
    return;
  }
  if (res.status === 401) {
    handleSessionExpired(); // 스트리밍 중 만료 → 자동 로그오프
    handlers.onError("unauthorized");
    return;
  }
  if (!res.ok || !res.body) {
    handlers.onError(`스트리밍 실패 (${res.status})`);
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";

  const dispatch = (raw: string) => {
    const lines = raw.split("\n");
    let event = "message";
    let data = "";
    for (const line of lines) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) data += line.slice(5).trim();
    }
    if (!data) return;
    let payload: unknown;
    try {
      payload = JSON.parse(data);
    } catch {
      return;
    }
    if (event === "chunk") handlers.onChunk((payload as { text: string }).text || "");
    else if (event === "done") handlers.onDone(payload as QueryDone);
    else if (event === "progress") handlers.onProgress?.(payload as StreamProgress);
    else if (event === "error") handlers.onError((payload as { message: string }).message || "error");
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx: number;
      // SSE 이벤트 구분자는 빈 줄(\n\n)
      while ((idx = buf.indexOf("\n\n")) !== -1) {
        const chunk = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        if (chunk.trim()) dispatch(chunk);
      }
    }
    if (buf.trim()) dispatch(buf);
  } catch (e) {
    if ((e as { name?: string })?.name === "AbortError") return; // 사용자 중지 — 조용히 종료
    handlers.onError("stream-read");
  }
}

// 답변 본문 끝에 붙은 "📚 참고 문서\n· ..." 블록을 분리(중복 표시 방지).
// 반환: { body: 출처 제거된 본문, sources: ["· [라벨] 제목: url", ...] }
export function splitSources(text: string): { body: string; sources: string[] } {
  const marker = "📚 참고 문서";
  const idx = (text || "").indexOf(marker);
  if (idx === -1) return { body: text || "", sources: [] };
  const body = text.slice(0, idx).trimEnd();
  const tail = text.slice(idx + marker.length);
  const sources = tail
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.startsWith("·"));
  return { body, sources };
}

// "· [고객사자료] 제목: https://..." → {label,title,url}
export function parseSource(s: string) {
  const cleaned = s.replace(/^·\s*/, "");
  const m = cleaned.match(/^\[(.+?)\]\s*(.*)$/);
  const label = m ? m[1] : "";
  const rest = m ? m[2] : cleaned;
  const ci = rest.lastIndexOf(": http");
  if (ci !== -1) {
    return { label, title: rest.slice(0, ci).trim(), url: rest.slice(ci + 2).trim() };
  }
  return { label, title: rest.trim(), url: null };
}
